import { request, setCustomOnResponseError, resetCustomOnResponseError } from './axios'
import {
  TADConstant,
  TADCoveredEntities,
  TADCoveredEntity,
  TADGroups,
  TADInternalUserCreate,
  TADMembershipGroups,
  TADMemberships,
  TADUser,
  TADUserCreate,
  TADUsers,
  TADUserUpdate,
  TGroupDetail,
  TADPPRoles,
} from '../type'
import { createDownloadLink } from '../utility/download-link'
import { dateFormat, Format } from '../utility/date-format'
import {
  TADBackground,
  TADInternalUser,
  TADInternalUserUpdate,
  TADMembershipsTemplate,
  TADMembershipTemplateDetail,
  TADStreamsAndApps,
  TMeta,
  TMetaItemValue,
} from '../type/ad-management'
import { customErrorResponse } from '../utility/custom-error-response'

export const ADManagement = {
  constants: async (): Promise<TADConstant> => {
    setCustomOnResponseError(customErrorResponse)
    try {
      const { data } = await request({
        url: '/jarvis_api/meta/',
      })

      const values = data['ORG_UNITS_NAMES'] ?? []

      return values.reduce((acc: TADConstant, item: TMetaItemValue) => {
        acc[item.label as keyof TADConstant] = item.value
        return acc
      }, {})
    } finally {
      resetCustomOnResponseError()
    }
  },

  meta: async (): Promise<TMeta> => {
    setCustomOnResponseError(customErrorResponse)
    try {
      const { data } = await request({
        url: '/jarvis_api/meta/',
      })
      return data
    } finally {
      resetCustomOnResponseError()
    }
  },

  user: {
    create: async (data: TADUserCreate): Promise<void> => {
      await request({
        url: '/jarvis_api/users/',
        method: 'post',
        data,
      })
    },

    createInternal: async (data: TADInternalUserCreate): Promise<void> => {
      await request({
        url: '/jarvis_api/users/internal/',
        method: 'post',
        data,
      })
    },

    list: async (search = '', page = 1): Promise<TADUsers> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const queryItems = []
        if (search) {
          queryItems.push(`UserLookupExtended=${search}`)
        }
        if (!search) {
          queryItems.push(`page_number=${page}`)
        }

        const {
          data: { total_count, total_pages, page_number, max_page_size, results },
        } = await request({
          url: `/jarvis_api/users/?${queryItems.join('&')}`,
        })

        return {
          total_count,
          total_pages,
          page_number,
          max_page_size,
          results,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    internalList: async (search = '', page = 1, signal?: AbortSignal): Promise<TADUsers> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const queryItems = [`page_number=${page}`, 'ShowMembership=True']
        if (search) {
          queryItems.push(`UserLookupExtended=${encodeURIComponent(search)}`)
        }

        const {
          data: { total_count, total_pages, page_number, max_page_size, results },
        } = await request({
          url: `/jarvis_api/users/in_folder/INT/?${queryItems.join('&')}`,
          signal,
        })

        return {
          total_count,
          total_pages,
          page_number,
          max_page_size,
          results,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    externalList: async (search = '', page = 1, signal?: AbortSignal): Promise<TADUsers> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const queryItems = [`page_number=${page}`, 'ShowMembership=True']
        if (search) {
          queryItems.push(`UserLookupExtended=${encodeURIComponent(search)}`)
        }

        const {
          data: { total_count, total_pages, page_number, max_page_size, results },
        } = await request({
          url: `/jarvis_api/users/in_folder/EXT/?${queryItems.join('&')}`,
          signal,
        })

        return {
          total_count,
          total_pages,
          page_number,
          max_page_size,
          results,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    listFiltered: async (
      search = '',
      folder: 'INT' | 'EXT' | '' = '',
      all = '',
      any = '',
      none = '',
      page = 1,
    ): Promise<TADMemberships> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const queryItems = [`page_number=${page}`]
        if (folder) queryItems.push(`FolderLookup=${folder}`)
        if (search) queryItems.push(`WildcardLookup=${encodeURIComponent(search)}`)
        if (all) queryItems.push(`AllGroups=${encodeURIComponent(all)}`)
        if (any) queryItems.push(`AnyOfGroups=${encodeURIComponent(any)}`)
        if (none) queryItems.push(`NoneOfGroups=${encodeURIComponent(none)}`)

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/users/by_groups/?${queryItems.join('&')}`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    detail: async (user: string): Promise<TADUser | TADInternalUser> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const { data } = await request({
          url: `/jarvis_api/users/detail/${user}/`,
        })

        return data
      } finally {
        resetCustomOnResponseError()
      }
    },

    enable: async (name: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: `/jarvis_api/users/enable/${name}/`,
          method: 'patch',
        })
      } finally {
        resetCustomOnResponseError()
      }
    },

    disable: async (name: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: `/jarvis_api/users/disable/${name}/`,
          method: 'patch',
        })
      } finally {
        resetCustomOnResponseError()
      }
    },

    updateInternal: async (user: TADInternalUserUpdate): Promise<TADInternalUser> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const { data } = await request({
          url: '/jarvis_api/users/internal/',
          method: 'patch',
          data: user,
        })

        return data
      } finally {
        resetCustomOnResponseError()
      }
    },

    update: async (user: TADUserUpdate): Promise<TADUser> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const { data } = await request({
          url: '/jarvis_api/users/',
          method: 'patch',
          data: user,
        })

        return data
      } finally {
        resetCustomOnResponseError()
      }
    },

    download: async (isExternal: boolean, search?: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const { data } = await request({
          responseType: 'arraybuffer',
          url: `/jarvis_api/users/${isExternal ? '' : 'internal/'}export_to_excel/${search ? `?UserLookupExtended=${search}` : ''}`,
          method: 'post',
        })
        createDownloadLink(
          data,
          `AD_${isExternal ? 'External' : 'Internal'}_Users_${dateFormat(new Date(), Format['YYYY-MM-DD__HH:MM:SS'])}.xlsx`,
        )
      } finally {
        resetCustomOnResponseError()
      }
    },
    exportByGroups: async (FolderLookup: 'INT' | 'EXT' | '', all = '', any = '', none = '', search?: string, page = 1): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const queryItems: string[] = [`page_number=${page}`]
        if (FolderLookup) queryItems.push(`FolderLookup=${FolderLookup}`)
        if (search) queryItems.push(`WildcardLookup=${encodeURIComponent(search)}`)
        if (all) queryItems.push(`AllGroups=${encodeURIComponent(all)}`)
        if (any) queryItems.push(`AnyOfGroups=${encodeURIComponent(any)}`)
        if (none) queryItems.push(`NoneOfGroups=${encodeURIComponent(none)}`)
        const { data } = await request({
          responseType: 'arraybuffer',
          url: `/jarvis_api/users/by_groups/export_to_excel/?${queryItems.join('&')}`,
          method: 'post',
        })
        createDownloadLink(data, `AD_${FolderLookup}_Users_${dateFormat(new Date(), Format['YYYY-MM-DD__HH:MM:SS'])}.xlsx`)
      } finally {
        resetCustomOnResponseError()
      }
    },
    delete: async (user: string, isExternal: boolean) => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: `/jarvis_api/users/${isExternal ? '' : 'internal/'}?Name=${user}`,
          method: 'delete',
        })
      } finally {
        resetCustomOnResponseError()
      }
    },
  },

  covered_entity: {
    list: async (search = '', page = 1, sharedGroups = false): Promise<TADCoveredEntities> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''
        const shared = sharedGroups ? '&SharedGroups=including' : ''

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/ce/?page_number=${page}${s}${shared}`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    detail: async (name: string): Promise<TADCoveredEntity> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const { data } = await request({
          url: `/jarvis_api/groups/memberships/member_of/${name}/?ReturnMembers=true`,
        })

        return data
      } finally {
        resetCustomOnResponseError()
      }
    },

    create: async (name: string, description: string, memberships: string[]): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: '/jarvis_api/groups/ce/',
          method: 'post',
          data: {
            Name: name,
            Description: description,
            Memberships: memberships,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },

    update: async (name: string, description: string, newName: string, memberships: string[]): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: '/jarvis_api/groups/ce/',
          method: 'patch',
          data: {
            Name: name,
            Description: description,
            NewName: newName,
            Memberships: memberships,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },
    download: async (search?: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const searchParam = search ? `GroupLookupExtended=${encodeURIComponent(search)}` : ''
        const url = `/jarvis_api/groups/ce/export_to_excel/${searchParam ? `?${searchParam}` : ''}`

        const response = await request({
          responseType: 'arraybuffer',
          url,
          method: 'post',
        })
        const contentDisposition = response.headers['content-disposition']
        let fileName = `AD_CE__${dateFormat(new Date(), Format['YYYY-MM-DD__HH:MM:SS'])}.xlsx`

        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1]
          }
        }

        createDownloadLink(response.data, fileName)
      } finally {
        resetCustomOnResponseError()
      }
    },
    delete: async (ce: string) => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: `/jarvis_api/groups/ce/?Name=${ce}`,
          method: 'delete',
        })
      } finally {
        resetCustomOnResponseError()
      }
    },
  },

  membership: {
    list: async (
      search = '',
      page = 1,
      type: '' | 'INT' | 'EXT' | 'TEMPLATE' = '',
      signal?: AbortSignal,
    ): Promise<TADMemberships | TADMembershipsTemplate> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        let url = ''
        let s = ''
        if (type === 'TEMPLATE') {
          s = search ? `&search_key=${encodeURIComponent(search)}` : ''
          url = `/jarvis_api/groups/memberships/templates/?page_number=${page}${s}`
        } else {
          s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''
          const t = type ? `&Type=${type}` : ''
          url = `/jarvis_api/groups/memberships/extended/ce_info/?page_number=${page}${s}${t}`
        }

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url,
          signal,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    listFiltered: async (
      search = '',
      folder: 'INT' | 'EXT' | '' = '',
      all = '',
      any = '',
      none = '',
      page = 1,
    ): Promise<TADMemberships> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const queryItems = [`page_number=${page}`]
        if (folder) queryItems.push(`FolderLookup=${folder}`)
        if (search) queryItems.push(`WildcardLookup=${encodeURIComponent(search)}`)
        if (all) queryItems.push(`AllGroups=${encodeURIComponent(all)}`)
        if (any) queryItems.push(`AnyOfGroups=${encodeURIComponent(any)}`)
        if (none) queryItems.push(`NoneOfGroups=${encodeURIComponent(none)}`)

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/memberships/by_groups/?${queryItems.join('&')}`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    listInternal: async (search = '', page = 1): Promise<TADMemberships> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/memberships/internal/?page_number=${page}${s}`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    detail: async (name: string, isRecursive = false): Promise<TADMembershipGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const { data } = await request({
          url: `/jarvis_api/groups/memberships/member_of/${name}/?ReturnMemberOf=true${isRecursive ? '&Recursive=true' : ''}`,
        })

        return data
      } finally {
        resetCustomOnResponseError()
      }
    },
    detailTemplate: async (id: string): Promise<TADMembershipTemplateDetail> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const { data } = await request({
          url: `/jarvis_api/groups/memberships/templates/${id}/`,
        })
        return data
      } finally {
        resetCustomOnResponseError()
      }
    },

    create: async (name: string, description: string, memberships: string[], type: 'INT' | 'EXT'): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: '/jarvis_api/groups/memberships/',
          method: 'post',
          data: {
            Name: name,
            Description: description,
            Memberships: memberships,
            Type: type,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },
    createTemplate: async (name: string, memberships: Record<string, { name: string; description: string }[]>): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: '/jarvis_api/groups/memberships/templates/',
          method: 'post',
          data: {
            name: name,
            memberships: memberships,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },

    update: async (name: string, newName: string, description: string, memberships: string[], type: 'INT' | 'EXT'): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: '/jarvis_api/groups/memberships/',
          method: 'patch',
          data: {
            Name: name,
            NewName: newName,
            Description: description,
            Memberships: memberships,
            Type: type,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },
    updateTemplate: async (
      id: string,
      memberships: Record<string, { name: string; description: string }[]>,
      name?: string,
    ): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      type UpdateTemplateData = { memberships: Record<string, { name: string; description: string }[]>; name?: string }
      try {
        const data: UpdateTemplateData = { memberships }
        if (name) {
          data.name = name
        }
        await request({
          url: `/jarvis_api/groups/memberships/templates/${id}/`,
          method: 'put',
          data,
        })
      } finally {
        resetCustomOnResponseError()
      }
    },
    deleteTemplate: async (id: string) => {
      setCustomOnResponseError(customErrorResponse)
      return request({
        method: 'delete',
        url: `/jarvis_api/groups/memberships/templates/${id}/`,
      }).finally(() => {
        resetCustomOnResponseError()
      })
    },
    download: async (isExternal: boolean, search?: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const typeParam = `Type=${isExternal ? 'EXT' : 'INT'}`
        const searchParam = search ? `GroupLookupExtended=${encodeURIComponent(search)}` : ''
        const query = [typeParam, searchParam].filter(Boolean).join('&')
        const url = `/jarvis_api/groups/memberships/export_to_excel/${query ? `?${query}` : ''}`

        const response = await request({
          responseType: 'arraybuffer',
          url,
          method: 'post',
        })
        const contentDisposition = response.headers['content-disposition']
        let fileName = `AD_${typeParam}_Membership_${dateFormat(new Date(), Format['YYYY-MM-DD__HH:MM:SS'])}.xlsx`

        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1]
          }
        }

        createDownloadLink(response.data, fileName)
      } finally {
        resetCustomOnResponseError()
      }
    },
    exportByGroups: async (FolderLookup: 'INT' | 'EXT' | '', all = '', any = '', none = '', search?: string, page = 1): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const queryItems: string[] = [`page_number=${page}`]
        if (FolderLookup) queryItems.push(`FolderLookup=${FolderLookup}`)
        if (search) queryItems.push(`WildcardLookup=${encodeURIComponent(search)}`)
        if (all) queryItems.push(`AllGroups=${encodeURIComponent(all)}`)
        if (any) queryItems.push(`AnyOfGroups=${encodeURIComponent(any)}`)
        if (none) queryItems.push(`NoneOfGroups=${encodeURIComponent(none)}`)
        const { data } = await request({
          responseType: 'arraybuffer',
          url: `/jarvis_api/groups/memberships/by_groups/export_to_excel/?${queryItems.join('&')}`,
          method: 'post',
        })
        createDownloadLink(data, `AD_${FolderLookup}_Memberships_${dateFormat(new Date(), Format['YYYY-MM-DD__HH:MM:SS'])}.xlsx`)
      } finally {
        resetCustomOnResponseError()
      }
    },

    delete: async (ce: string, isExternal: boolean) => {
      const typeParam = `Type=${isExternal ? 'EXT' : 'INT'}`
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: `/jarvis_api/groups/memberships/?${typeParam}&Name=${ce}`,
          method: 'delete',
        })
      } finally {
        resetCustomOnResponseError()
      }
    },
  },

  group: {
    all: async (search = '', page = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/all/?page_number=${page}${s}`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    covered_entity: async (search = '', page = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/ce/?SharedGroups=only&page_number=${page}${s}`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    ce: async (search = '', page = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''
        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/ce/?page_number=${page}${s}`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    ce_groups: async (search = '', page = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/ce/?SharedGroups=only&page_number=${page}${s}`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    pharmacies: async (search = '', page = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/pharmacies/?page_number=${page}${s}`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    pharmacy_groups: async (search = '', page = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/pharmacies/?SharedGroups=only&page_number=${page}${s}`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    referral_types: async (search = '', page = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/referral_types/?&page_number=${page}${s}`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    referral_types_groups: async (search = '', page = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''
        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/referral_types/?SharedGroups=only&page_number=${page}${s}`,
        })
        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    distributor: async (search = '', page = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/distributors/?page_number=${page}${s}`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    distributor_groups: async (search = '', page = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/distributors/?SharedGroups=only&page_number=${page}${s}`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    qlik_app: async (search = '', page = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/qlik/apps/?page_number=${page}${s}`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    qlik_stream: async (search = '', page = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/qlik/streams/?page_number=${page}${s}`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    qlik_groups: async (search = '', page = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/qlik/shared_groups/?SharedGroups=only&page_number=${page}${s}`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    reports: async (search = '', page = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/qlik_based_reports/?page_number=${page}${s}`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    report_groups: async (search = '', page = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/qlik_based_reports/shared_groups/?SharedGroups=only&page_number=${page}${s}`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    pharmacy: async (search = '', page = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/pharmacies/?page_number=${page}${s}&SharedGroups=including`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    omit: async (search = '', page = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/omissions/?page_number=${page}${s}&SharedGroups=including`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    omit_groups: async (search = '', page = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/omissions/?page_number=${page}${s}&SharedGroups=only`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    distributors: async (search = '', page = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/distributors/?page_number=${page}${s}&SharedGroups=including`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    manufacturers: async (search = '', page = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/manufacturers/?page_number=${page}${s}&SharedGroups=including`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    measures: async (search = '', page = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/omissions/?page_number=${page}${s}`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    other: async (search = '', page = 1): Promise<TADGroups> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/others/?page_number=${page}${s}`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    all_details: async (search = ''): Promise<TGroupDetail> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const { data } = await request({
          url: `/jarvis_api/groups/details/all/${search}/`,
        })

        return data
      } finally {
        resetCustomOnResponseError()
      }
    },
  },
  streamsAndApplications: {
    list: async (search = '', page = 1, streamsOnly: boolean, signal?: AbortSignal): Promise<TADStreamsAndApps> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const queryItems = [`page_number=${page}`]
        if (search) {
          queryItems.push(`GroupLookupExtended=${encodeURIComponent(search)}`)
        }

        const {
          data: { total_count, total_pages, page_number, max_page_size, results },
        } = await request({
          url: `/jarvis_api/groups/qlik/${streamsOnly ? 'streams' : 'apps'}/?${queryItems.join('&')}`,
          signal,
        })

        return {
          total_count,
          total_pages,
          page_number,
          max_page_size,
          results,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    update: async (name: string, description: string, newName: string, memberships: string[], isStream: boolean): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: `/jarvis_api/groups/qlik/${isStream ? 'streams' : 'apps'}/`,
          method: 'patch',
          data: {
            Name: name,
            Description: description,
            NewName: newName,
            Memberships: memberships,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },

    create: async (name: string, description: string, memberships: string[], isStream: boolean): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: `/jarvis_api/groups/qlik/${isStream ? 'streams' : 'apps'}/`,
          method: 'post',
          data: {
            Name: name,
            Description: description,
            Memberships: memberships,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },
    download: async (isStream: boolean, search?: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const searchParam = search ? `GroupLookupExtended=${encodeURIComponent(search)}` : ''
        const url = `/jarvis_api/groups/qlik/${isStream ? 'streams' : 'apps'}/export_to_excel/${searchParam ? `?${searchParam}` : ''}`

        const response = await request({
          responseType: 'arraybuffer',
          url,
          method: 'post',
        })
        const contentDisposition = response.headers['content-disposition']
        let fileName = `AD_CE__${dateFormat(new Date(), Format['YYYY-MM-DD__HH:MM:SS'])}.xlsx`

        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1]
          }
        }

        createDownloadLink(response.data, fileName)
      } finally {
        resetCustomOnResponseError()
      }
    },
    delete: async (name: string, isStream: boolean) => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: `/jarvis_api/groups/qlik/${isStream ? 'streams' : 'apps'}/?Name=${name}`,
          method: 'delete',
        })
      } finally {
        resetCustomOnResponseError()
      }
    },
  },
  sharedGroups: {
    list: async (search = '', page = 1, sufix: string, sharedGroupsParam: string | null, signal?: AbortSignal): Promise<TADMemberships> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const queryItems = [`page_number=${page}`]
        if (search) {
          queryItems.push(`GroupLookupExtended=${encodeURIComponent(search)}`)
        }
        if (sharedGroupsParam) {
          queryItems.push(`SharedGroups=${sharedGroupsParam}`)
        }
        const {
          data: { total_count, total_pages, page_number, max_page_size, results },
        } = await request({
          url: `/jarvis_api/groups/${sufix}?${queryItems.join('&')}`,
          signal,
        })

        return {
          total_count,
          total_pages,
          page_number,
          max_page_size,
          results,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },

    update: async (name: string, description: string, newName: string, memberships: string[], sufix: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: `/jarvis_api/groups/${sufix}`,
          method: 'patch',
          data: {
            Name: name,
            Description: description,
            NewName: newName,
            Memberships: memberships,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },

    create: async (name: string, description: string, memberships: string[], sufix: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: `/jarvis_api/groups/${sufix}`,
          method: 'post',
          data: {
            Name: name,
            Description: description,
            Memberships: memberships,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },
    download: async (queryUrl: string, search?: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const searchParam = search ? `GroupLookupExtended=${encodeURIComponent(search)}` : ''
        const sharedGroupsParam = 'SharedGroups=only'
        const query = [searchParam, sharedGroupsParam].filter(Boolean).join('&')
        const url = `${queryUrl}${query ? `?${query}` : ''}`

        const response = await request({
          responseType: 'arraybuffer',
          url,
          method: 'post',
        })
        const contentDisposition = response.headers['content-disposition']
        let fileName = `AD_SharedGroups__${dateFormat(new Date(), Format['YYYY-MM-DD__HH:MM:SS'])}.xlsx`

        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/)
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1]
          }
        }

        createDownloadLink(response.data, fileName)
      } finally {
        resetCustomOnResponseError()
      }
    },
    delete: async (name: string, sufix: string) => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: `/jarvis_api/groups/${sufix}/?Name=${name}`,
          method: 'delete',
        })
      } finally {
        resetCustomOnResponseError()
      }
    },
  },
  entities: {
    list: async (search = '', page = 1, sufix: string, entitiesParam: string | null, signal?: AbortSignal): Promise<TADMemberships> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const queryItems = [`page_number=${page}`]
        if (search) {
          queryItems.push(`GroupLookupExtended=${encodeURIComponent(search)}`)
        }
        if (entitiesParam) {
          queryItems.push(`SharedGroups=${entitiesParam}`)
        }
        const {
          data: { total_count, total_pages, page_number, max_page_size, results },
        } = await request({
          url: `/jarvis_api/groups/${sufix}?${queryItems.join('&')}`,
          signal,
        })

        return {
          total_count,
          total_pages,
          page_number,
          max_page_size,
          results,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    update: async (id: string, name: string, newName: string, memberships: string[], type: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: `/jarvis_api/groups/${type}/`,
          method: 'patch',
          data: {
            Name: id,
            Description: name,
            NewName: newName,
            Memberships: memberships,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },

    create: async (name: string, description: string, memberships: string[], type: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: `/jarvis_api/groups/${type}/`,
          method: 'post',
          data: {
            Name: name,
            Description: description,
            Memberships: memberships,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },
    download: async (queryUrl: string, search?: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const searchParam = search ? `GroupLookupExtended=${encodeURIComponent(search)}` : ''
        const url = `${queryUrl}${searchParam ? `?${searchParam}` : ''}`

        const response = await request({
          responseType: 'arraybuffer',
          url,
          method: 'post',
        })
        const contentDisposition = response.headers['content-disposition']
        let fileName = `AD_Entities_${dateFormat(new Date(), Format['YYYY-MM-DD__HH:MM:SS'])}.xlsx`

        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1]
          }
        }

        createDownloadLink(response.data, fileName)
      } finally {
        resetCustomOnResponseError()
      }
    },
    delete: async (id: string, type: string) => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: `/jarvis_api/groups/${type}/?Name=${id}`,
          method: 'delete',
        })
      } finally {
        resetCustomOnResponseError()
      }
    },
  },
  pp_roles: {
    list: async (search = '', page = 1, sharedGroups = false): Promise<TADPPRoles> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        const s = search ? `&GroupLookupExtended=${encodeURIComponent(search)}` : ''
        const shared = sharedGroups ? '&SharedGroups=including' : ''

        const {
          data: { results, max_page_size, page_number, total_count, total_pages },
        } = await request({
          url: `/jarvis_api/groups/pp_roles/?page_number=${page}${s}${shared}`,
        })

        return {
          results,
          max_page_size,
          page_number,
          total_count,
          total_pages,
        }
      } finally {
        resetCustomOnResponseError()
      }
    },
    create: async (name: string, description: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: '/jarvis_api/groups/pp_roles/',
          method: 'post',
          data: {
            Name: name,
            Description: description,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },
    update: async (name: string, description: string, newName: string): Promise<void> => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: '/jarvis_api/groups/pp_roles/',
          method: 'patch',
          data: {
            Name: name,
            Description: description,
            NewName: newName,
          },
        })
      } finally {
        resetCustomOnResponseError()
      }
    },
    delete: async (name: string) => {
      setCustomOnResponseError(customErrorResponse)
      try {
        await request({
          url: `/jarvis_api/groups/pp_roles/?Name=${name}`,
          method: 'delete',
        })
      } finally {
        resetCustomOnResponseError()
      }
    },
  },

  background: async (): Promise<TADBackground> => {
    const { data } = await request({
      url: '/jarvis_api/background/',
    })
    return data
  },
}
